<template>
  <template v-if="enableThumbnails">
    <div v-if="thumbnailUrl" v-bind="$attrs" class="canvas-thumbnail flex items-center justify-center">
      <CImage
        :aiImgDescription="item.name"
        broken-image-size="20px"
        class="object-cover w-full h-full"
        :class="imageClass ? imageClass : 'rounded-2'"
        :height="height"
        object-fit="cover"
        :preview-disabled="previewDisabled"
        :src="thumbnailUrl"
        :style="{
          backgroundColor: themeVars.primary6,
          border: `1px solid ${themeVars.primary6}`,
        }"
        :width="width"
      />
    </div>
    <div
      v-else
      v-bind="$attrs"
      class="canvas-thumbnail rounded-2 flex items-center justify-center"
      :class="imageClass ? imageClass : 'rounded-2'"
      :style="[
        { width: `${width + 2}px`, height: `${height + 2}px` },
        imageClass
          ? {}
          : {
              backgroundColor: themeVars.transparent,
              border: `1px solid ${themeVars.primary6}`,
            },
      ]"
    >
      <CIcon :color="iconProps.color" :fa-type="iconProps.faType" :icon="iconProps.icon" :size="`${iconSize}px`" />
    </div>
    <slot></slot>
  </template>
</template>

<script setup lang="ts">
import type { CanvasRetrieve, Canvas } from '@openapi'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import { computed } from 'vue'
import CIcon from '../CIcon/CIcon.vue'
import CImage from '../CImage/CImage.vue'

export interface CanvasIconProps {
  icon: string
  faType: FontAwesomeType
  color: string
}

const props = withDefaults(
  defineProps<{
    thumbnailUrl?: string | null
    item: CanvasRetrieve | Canvas
    themeVars: Record<string, string>
    enableThumbnails?: boolean
    isFolder?: boolean
    width?: number
    height?: number
    iconSize?: number
    previewDisabled?: boolean
    imageClass?: string
  }>(),
  {
    thumbnailUrl: null,
    enableThumbnails: true,
    width: 80,
    height: 53,
    iconSize: 20,
    isFolder: false,
    previewDisabled: true,
  },
)

const iconProps = computed(() => getCanvasTypeIcon(props.item, props.themeVars, props.isFolder))

function getCanvasTypeIcon(
  item: CanvasRetrieve | Canvas,
  themeVars: Record<string, string>,
  isFolder: boolean,
): CanvasIconProps {
  if (isFolder) {
    return {
      icon: 'folder',
      faType: 'fa',
      color: themeVars.primary4,
    }
  }

  if (item.is_template && item.is_section) {
    return {
      icon: 'solid-bring-forward-circle-plus',
      faType: 'fa-kit',
      color: themeVars.primary4,
    }
  }

  if (item.is_template) {
    return {
      icon: 'solid-presentation-screen-circle-plus',
      faType: 'fa-kit',
      color: themeVars.primary4,
    }
  }

  if (item.is_section) {
    return {
      icon: 'bring-forward',
      faType: 'fa',
      color: themeVars.primary4,
    }
  }

  if (item.is_block) {
    return {
      icon: 'objects-column',
      faType: 'fa',
      color: themeVars.primary4,
    }
  }

  return {
    icon: 'presentation',
    faType: 'fa',
    color: themeVars.text3,
  }
}
</script>
